import React, { useState, useEffect } from "react";
import { Collapse, Form, Button } from "antd";
import LogoCheckOut from "../../assets/images/logo-checkout.svg";
import CoineIcon from "../../assets/images/header-coin-icon.svg";
import PersonalInfo from "./PersonalInfo";
import AddressSection from "./AddressSection";
import PaymentMethods from "./PaymentMethods";
import PromocodeSection from "./PromocodeSection";
import CartSummary from "./CartSummary";
import { useStore } from "../../context/StoreContext";
import { chargePayment } from "../../helper/functions";
import { useLocation } from "react-router-dom";
import { ShowToast, Severty } from "../../helper/toast";
import { useNavigate } from "react-router-dom";
import UniversalModal from "../../components/UniversalModal";
import CommonBreadcrumb from "../../components/BreadCrumb";

const CheckOutIndex = ({}) => {
  const { loadAddress, loadCartItems, placeOrder } = useStore();
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoCode, setPromoCode] = useState();
  const [address, setAddress] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const location = useLocation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [savedInfo, setSavedInfo] = useState({
    addInfo: false,
    payInfo: false,
  });
  const userProfileData = JSON.parse(localStorage.getItem("userProfile"));
  const walletSummaryData = JSON.parse(localStorage.getItem("walletSummary"));

  const [isModal, setIsModal] = useState({
    open: false,
    title: "",
    message: "",
    buttonText: "",
  });
  // -------------- handlers -------------
  const fetchAddress = async () => {
    const response = await loadAddress();
    setAddress(response.data);
  };

  const loadCartItemsData = async () => {
    const response = await loadCartItems();
    setCartItems(response?.data);
  };

  const handleSubmit = async () => {
    try {
      if (savedInfo.addInfo === false || savedInfo.payInfo === false) {
        ShowToast(
          "Please check payment and address confirmation",
          Severty.ERROR
        );
      }
      const baseUrl = window.location.origin;
      const values = await form.validateFields();
      values.cartId = cartItems?.cartId;
      // save ordered data in localstorage
      localStorage.setItem(
        "checkoutData",
        JSON.stringify({
          cartId: cartItems?.cartId,
          addressId: form.getFieldValue("addressId"),
          method: form.getFieldValue("method"),
          promoCode: promoApplied ? promoCode : null,
        })
      );

      if (!(values.cartId && values.method)) {
        return;
      }

      if (values.method === "token") {
        // ------- call the post api -------]
        const resultPlaceOrder = await placeOrder({
          cartId: cartItems?.cartId,
          addressId: form.getFieldValue("addressId"),
          method: form.getFieldValue("method"),
          promoCode: promoApplied ? promoCode : null,
        });

        if (resultPlaceOrder.status === 200) {
          setIsModal({
            open: true,
            title: "Order Placed",
            message: resultPlaceOrder.message,
            buttonText: "Back to Home",
          });
          navigate("/order");
        }
        return;
      }
      // ----------- Here called api to U Payments -----------
      const data = {
        order: {
          id: values.cartId,
          description: `Payment for Order ${"ORD" + Date.now()}`,
          currency: "KWD",
          amount: cartItems?.grandTotal,
        },
        paymentGateway: {
          src: values.method,
        },
        language: "en",
        tokens: {
          customerUniqueToken: Number(userProfileData?.customerUniqueToken),
        },
        reference: {
          id: Date.now().toString(),
        },
        customer: {
          uniqueId: userProfileData?._id,
          name: userProfileData?.name,
          email: userProfileData?.email,
          mobile: userProfileData?.phone,
        },
        returnUrl: `${baseUrl}/checkout`,
        cancelUrl: `${baseUrl}/checkout`,
        notificationUrl: `${baseUrl}/checkout`,
      };
      const response = await chargePayment(data);
      const link = response.data.link;
      window.location.href = link;
    } catch (err) {
      ShowToast(err.message, Severty.ERROR);
      console.error("❌ Validation failed:", err);
    }
  };

  // --------- call the post api if payment is successfull -------------
  const handlePlaceOrder = async (paymentResult) => {
    const storedData = JSON.parse(localStorage.getItem("checkoutData"));
    const data = {
      cartId: paymentResult.requested_order_id,
      addressId: storedData.addressId,
      method: paymentResult.payment_type,
      gatewayResponse: paymentResult,
      promoCode: storedData.promoCode,
    };
    await placeOrder(data);
    navigate("/order");
  };
  // -------------- useEffect -------------
  useEffect(() => {
    loadCartItemsData();
    fetchAddress();
  }, []);
  // when payment is confirmed
  useEffect(() => {
    if (!location.search) return;
    const paymentResult = Object.fromEntries(
      new URLSearchParams(location.search)
    );
    if (
      paymentResult.result === "CAPTURED" ||
      paymentResult.result === "NOT CAPTURED"
    ) {
      ShowToast("Payment Successful", Severty.SUCCESS);
      handlePlaceOrder(paymentResult);
    } else {
      ShowToast("Payment Failed", Severty.ERROR);
    }
  }, [location.search]);

  useEffect(() => {
    if (!promoCode || promoApplied === true) return;

    const percentValue = Number(promoCode.match(/\d+/)[0]);
    const discountAmount = (cartItems?.grandTotal * percentValue) / 100;
    const finalTotal = cartItems?.grandTotal - discountAmount;

    setCartItems((prev) => ({
      ...prev,
      grandTotal: Number(finalTotal.toFixed(2)),
    }));

    setPromoApplied(true); // 🔥 Apply only once
  }, [promoCode]);

  return (
    <div className="main-checkout">
      <div className="checkout-header-main">
        <h6>Checkout</h6>
        <div className="checkout-header">
          <img src={LogoCheckOut} alt="logo" />
        </div>
        <div className="coine-countbox">
          <img src={CoineIcon} alt="coine" />
          <span>{walletSummaryData?.wallet?.total_balance}</span>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div style={{ padding: "20px" }}>
          <CommonBreadcrumb
            items={[
              { title: "Home", url: "/home" },
              { title: "Stores", url: "/stores" },
            ]}
          />
        </div>
        <div className="checkout-container">
          <div className="checkout-wrapper">
            <div className="checkout-left">
              <Collapse
                expandIconPosition="end"
                className="checkout-collapse"
                defaultActiveKey={["1"]}
                bordered={false}
              >
                <Collapse.Panel header="Personal Information" key="1">
                  <PersonalInfo />
                </Collapse.Panel>

                <Collapse.Panel header="Delivery Address" key="2">
                  <AddressSection
                    savedInfo={savedInfo}
                    setSavedInfo={setSavedInfo}
                    address={address}
                    fetchAddress={fetchAddress}
                  />
                </Collapse.Panel>

                <Collapse.Panel header="Payment Methods" key="3">
                  <PaymentMethods
                    savedInfo={savedInfo}
                    setSavedInfo={setSavedInfo}
                    form={form}
                  />
                </Collapse.Panel>

                <Collapse.Panel header="Promocode" key="4">
                  <PromocodeSection
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                  />
                </Collapse.Panel>
              </Collapse>
            </div>

            <div className="checkout-right">
              <CartSummary
                checkoutData={cartItems}
                handleSubmit={handleSubmit}
                promoCode={promoCode}
              />
            </div>
          </div>
        </div>
      </Form>
      {/* Successfull order message */}
      <UniversalModal
        open={isModal.open}
        onClose={() => setIsModal(false)}
        title={isModal.title}
        message={isModal.message}
        footerButtonLabel={isModal.buttonText}
      />
    </div>
  );
};

export default CheckOutIndex;
